import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  Injector,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { A11yModule } from '@angular/cdk/a11y';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject, Subscription, debounceTime, filter, map } from 'rxjs';
import { CiMappingAssignDialogComponent } from '../../dashboard/ci-mapping/ci-mapping-assign-dialog.component';
import { assignmentFailure, CiMappingService } from '../../dashboard/ci-mapping/ci-mapping.service';
import { ReportTestItem, ReportTestPage } from '../../dashboard/ci-mapping/ci-mapping.model';
import { DefectRefChipsComponent } from '../defect-ref-chips/defect-ref-chips.component';
import { LiveBugDialogComponent } from '../../dashboard/testlauf-runner/live-bug-dialog/live-bug-dialog.component';
import { CreateBugFromStepResultRequest } from '../../models/test-run.model';
import { TestCase } from '../../models/test-case.model';
import { CaseResultBugRun, caseResultBugFlow } from '../../services/case-result-bug-flow';
import { CodeIdChipComponent } from '../code-id-chip/code-id-chip.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { FilterChipComponent } from '../filter-chip/filter-chip.component';
import { ResPillComponent } from '../res-pill/res-pill.component';
import {
  REPORT_TEST_CHIPS,
  REPORT_TEST_SEARCH_DELAY_MS,
  REPORT_TEST_SEARCH_MAX,
  ReportTestChip,
  ReportTestChipCounts,
  reportTestChipCounts,
  reportTestChipFromAddress,
  reportTestChipToPreselect,
  reportTestCountKey,
  reportTestFilterEmptyKey,
  reportTestFilterQuery,
  reportTestMatchesFilter,
  reportTestSearchTerm,
} from './report-test-filter';
import {
  ReportTestLiveCounts,
  TestRunLiveEvent,
  TestlaufLiveFeedService,
} from '../../services/testlauf-live-feed.service';
import { reportTestClassname, ReportTestStatusMark, reportTestStatusMark, reportTestTime } from './report-test-view';

let nextTableId = 0;

const PAGE_SIZE = 100;

const LIVE_STATUS_INTERVAL_MS = 10_000;

const MAX_HELD_LIVE_EVENTS = 500;

interface LiveStatus {
  reported: number;
  failed: number;
}

type LoadFailure = 'first' | 'more';

@Component({
  selector: 'app-report-test-table',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    A11yModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslatePipe,
    CodeIdChipComponent,
    ResPillComponent,
    FilterChipComponent,
    ConfirmDialogComponent,
    CiMappingAssignDialogComponent,
    LiveBugDialogComponent,
    DefectRefChipsComponent,
  ],
  templateUrl: './report-test-table.component.html',
  styleUrl: './report-test-table.component.scss',
})
export class ReportTestTableComponent {
  private readonly ciMapping = inject(CiMappingService);
  private readonly feed = inject(TestlaufLiveFeedService, { optional: true });
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly projectId = input.required<string>();
  readonly runId = input.required<string>();
  readonly executionId = input.required<string>();
  readonly completed = input(false);
  readonly canWrite = input(false);
  readonly bugRun = input<CaseResultBugRun | null>(null);

  readonly assignmentChanged = output<void>();
  readonly bugCreated = output<{ caseResultId: string; key: string }>();

  readonly items = signal<ReportTestItem[]>([]);
  readonly loading = signal(true);
  readonly loadingMore = signal(false);
  readonly failure = signal<LoadFailure | null>(null);
  private readonly hasReportData = signal(true);
  private readonly filteredTotal = signal(0);
  private readonly live = signal(false);

  readonly hasMore = computed(() => this.items().length < this.filteredTotal());
  readonly showsLegacyHint = computed(() => this.completed() && !this.hasReportData() && this.items().length > 0);
  readonly showsReadonlyHint = computed(() => this.completed() && this.live());
  readonly emptyKey = computed(
    () =>
      reportTestFilterEmptyKey(this.counts(), this.activeChip(), this.search()) ??
      (this.completed() ? 'reportTestTable.emptyFinished' : 'reportTestTable.empty'),
  );

  readonly chips = REPORT_TEST_CHIPS;
  readonly searchMax = REPORT_TEST_SEARCH_MAX;
  private readonly tableId = nextTableId++;
  readonly searchId = `report-test-search-${this.tableId}`;
  readonly activeChip = signal<ReportTestChip | null>(
    reportTestChipFromAddress(inject(ActivatedRoute, { optional: true })?.snapshot?.queryParamMap?.get('filter')),
  );
  readonly search = signal('');
  readonly counts = signal<ReportTestChipCounts | null>(null);
  private readonly searchInput = new Subject<string | null>();
  private readonly searchField = viewChild<ElementRef<HTMLInputElement>>('searchField');

  private readonly expanded = signal<ReadonlySet<string>>(new Set());

  /**
   * Nur Report-Zeilen tragen IDs, die der Server zuordnen kann; Live-Zeilen und die
   * Fall-Ergebnisse eines Altlaufs ersetzt erst ein Report-Import.
   */
  readonly canAssign = computed(() => this.canWrite() && !this.live() && this.hasReportData());
  readonly assignTarget = signal<ReportTestItem | null>(null);
  readonly assigning = signal(false);
  readonly assignErrorKey = signal<string | null>(null);
  readonly unassignTarget = signal<ReportTestItem | null>(null);
  readonly unassigning = signal(false);
  readonly actionErrorKey = signal<string | null>(null);
  private unassignTrigger: HTMLElement | null = null;
  private readonly chipGroup = viewChild<ElementRef<HTMLElement>>('chipGroup');
  private readonly confirmHost = viewChild<ElementRef<HTMLElement>>('confirmHost');

  protected readonly bug = caseResultBugFlow();
  readonly bugChoice = this.bug.choice;
  private readonly canCreateBug = computed(() => this.canAssign() && this.bug.canCreate() && this.bugRun() !== null);

  readonly bugDialogExistingKey = computed(() => {
    const dialog = this.bug.dialog();
    return dialog
      ? (this.items().find((item) => item.caseResultId === dialog.caseResultId)?.linkedBugKey ?? null)
      : null;
  });

  readonly bugDialogExistingUrl = computed(() => {
    const dialog = this.bug.dialog();
    const key = this.bugDialogExistingKey();
    return dialog && key ? this.bug.urlFor(dialog.caseResultId, key) : null;
  });

  readonly liveStatus = signal<LiveStatus | null>(null);
  readonly liveStatusKey = computed(() =>
    this.liveStatus()?.reported === 1 ? 'reportTestTable.liveStatusOne' : 'reportTestTable.liveStatusOther',
  );

  private page = 0;
  private request?: Subscription;
  private heldLiveEvents: TestRunLiveEvent[] | null = null;
  private heldLiveEventsOverflowed = false;
  private pendingLiveStatus: LiveStatus | null = null;
  private liveStatusAnnouncedAt = Number.NEGATIVE_INFINITY;
  private liveStatusTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    let opened = false;
    effect(() => {
      this.projectId();
      this.runId();
      this.executionId();
      untracked(() => {
        if (opened) this.resetForOtherExecution();
        opened = true;
        this.load();
      });
    });
    this.destroyRef.onDestroy(() => {
      this.request?.unsubscribe();
      clearTimeout(this.liveStatusTimer);
    });
    this.watchSearch(this.destroyRef);
    this.followLiveFeed();
  }

  selectChip(chip: ReportTestChip): void {
    if (chip === this.activeChip()) return;
    this.activeChip.set(chip);
    this.actionErrorKey.set(null);
    this.load();
  }

  onSearch(value: string): void {
    this.searchInput.next(value);
  }

  countKey(count: number): string {
    return reportTestCountKey(count);
  }

  loadMore(): void {
    this.loadingMore.set(true);
    this.failure.set(null);
    this.fetch(this.page + 1);
  }

  retry(): void {
    if (this.failure() === 'more') {
      this.loadMore();
    } else {
      this.load();
    }
  }

  classname(item: ReportTestItem): string | null {
    return reportTestClassname(item);
  }

  statusMark(item: ReportTestItem): ReportTestStatusMark {
    return reportTestStatusMark(item.status);
  }

  statusLabelKey(item: ReportTestItem): string {
    return `reportTestTable.status.${item.status ?? 'unknown'}`;
  }

  failureLine(item: ReportTestItem): string | null {
    if (item.status !== 'FAILED' || !item.errorMessage) return null;
    return item.errorMessage.split(/\r?\n/)[0].trim() || null;
  }

  isExpanded(item: ReportTestItem): boolean {
    return this.expanded().has(item.id);
  }

  errorId(item: ReportTestItem): string {
    return `report-test-error-${this.tableId}-${item.id}`;
  }

  toggleError(item: ReportTestItem): void {
    this.expanded.update((current) => {
      const next = new Set(current);
      if (!next.delete(item.id)) next.add(item.id);
      return next;
    });
  }

  time(item: ReportTestItem): string {
    return reportTestTime(item.durationMs);
  }

  offersBug(item: ReportTestItem): boolean {
    return (
      this.canCreateBug() && item.status === 'FAILED' && !!item.caseResultId && !!item.testCaseKey && !item.linkedBugKey
    );
  }

  bugUrl(item: ReportTestItem): string | null {
    return this.bug.urlFor(item.caseResultId ?? '', item.linkedBugKey ?? '');
  }

  openBug(item: ReportTestItem): void {
    const run = this.bugRun();
    if (!run || !item.caseResultId || !item.testCaseKey) return;
    this.bug.open(this.projectId(), item.caseResultId, { autoId: item.testCaseKey, testName: item.name, ...run });
  }

  /** Haengt an der Zeile inzwischen ein Vorgang, ist der Knopf weg; der Fokus geht dann auf den Absprung. */
  closeBug(): void {
    const linked = this.bugDialogExistingKey() !== null;
    const dialog = this.bug.close();
    if (dialog && linked) this.focusBugLink(dialog.caseResultId);
  }

  confirmBug(request: CreateBugFromStepResultRequest): void {
    this.bug.confirm(this.projectId(), request, {
      created: (caseResultId, key) => {
        this.markBugLinked(caseResultId, key);
        this.closeBug();
        this.bugCreated.emit({ caseResultId, key });
      },
      // Der Server nennt im 409 keinen Schluessel; die neu geladene Zeile bringt ihn in den Dialog.
      alreadyLinked: () => this.load(),
    });
  }

  markBugLinked(caseResultId: string, key: string): void {
    this.items.update((current) =>
      current.map((item) => (item.caseResultId === caseResultId ? { ...item, linkedBugKey: key } : item)),
    );
  }

  private focusBugLink(caseResultId: string): void {
    afterNextRender(
      () =>
        this.host.nativeElement
          .querySelector<HTMLElement>(
            `[data-testid="report-test-bug-link"][data-case-result="${CSS.escape(caseResultId)}"]`,
          )
          ?.focus(),
      { injector: this.injector },
    );
  }

  openAssign(item: ReportTestItem): void {
    this.actionErrorKey.set(null);
    this.assignErrorKey.set(null);
    this.assignTarget.set(item);
  }

  cancelAssign(): void {
    this.assignTarget.set(null);
    this.assignErrorKey.set(null);
  }

  onPicked(testCase: TestCase): void {
    const target = this.assignTarget();
    if (!target || this.assigning()) return;
    this.assigning.set(true);
    this.assignErrorKey.set(null);
    this.ciMapping
      .assign(this.projectId(), this.runId(), target.id, testCase.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.assigning.set(false);
          this.assignTarget.set(null);
          this.reloadAfterAction();
        },
        error: (error: unknown) => {
          this.assigning.set(false);
          const failure = assignmentFailure(error, 'assign');
          if (!failure.stale) {
            this.assignErrorKey.set(failure.messageKey);
            return;
          }
          this.assignTarget.set(null);
          this.actionErrorKey.set(failure.messageKey);
          this.reloadAfterAction();
        },
      });
  }

  openUnassign(item: ReportTestItem): void {
    this.actionErrorKey.set(null);
    this.unassignTrigger = document.activeElement as HTMLElement | null;
    this.unassignTarget.set(item);
    // Die Rückfrage bringt keine eigene Fokusführung mit; ohne diesen Schritt bliebe der
    // Fokus auf dem Knopf hinter dem Dialog.
    afterNextRender(() => this.confirmHost()?.nativeElement.querySelector<HTMLElement>('[role="dialog"]')?.focus(), {
      injector: this.injector,
    });
  }

  cancelUnassign(): void {
    if (this.unassigning()) return;
    this.unassignTarget.set(null);
    this.restoreUnassignFocus();
  }

  confirmUnassign(): void {
    const target = this.unassignTarget();
    if (!target || this.unassigning()) return;
    this.unassigning.set(true);
    this.ciMapping
      .unassign(this.projectId(), this.runId(), target.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.unassigning.set(false);
          this.unassignTarget.set(null);
          this.reloadAfterAction();
        },
        error: (error: unknown) => {
          this.unassigning.set(false);
          this.unassignTarget.set(null);
          const failure = assignmentFailure(error, 'unassign');
          this.actionErrorKey.set(failure.messageKey);
          if (failure.stale) {
            this.reloadAfterAction();
          } else {
            this.restoreUnassignFocus();
          }
        },
      });
  }

  private restoreUnassignFocus(): void {
    if (this.unassignTrigger?.isConnected) this.unassignTrigger.focus();
    this.unassignTrigger = null;
  }

  // Nach einer Änderung verschwindet die Zeile samt ihrem Knopf womöglich aus dem Filter;
  // der Fokus geht deshalb auf den gewählten Chip, der stehen bleibt.
  private reloadAfterAction(): void {
    this.unassignTrigger = null;
    this.load();
    this.assignmentChanged.emit();
    afterNextRender(
      () =>
        this.chipGroup()
          ?.nativeElement.querySelector<HTMLElement>(`[data-testid="report-filter-${this.activeChip()}"] button`)
          ?.focus(),
      { injector: this.injector },
    );
  }

  private load(): void {
    this.items.set([]);
    this.loading.set(true);
    this.failure.set(null);
    this.fetch(0);
  }

  /** Filter und Suche galten dem vorigen Durchlauf; die Vorauswahl entscheidet fuer den neuen neu. */
  private resetForOtherExecution(): void {
    this.activeChip.set(null);
    this.counts.set(null);
    this.search.set('');
    this.searchInput.next(null);
    this.expanded.set(new Set());
    this.assignTarget.set(null);
    this.unassignTarget.set(null);
    this.bug.close();
    this.actionErrorKey.set(null);
    const field = this.searchField();
    if (field) field.nativeElement.value = '';
    this.discardHeldLiveEvents();
  }

  private fetch(page: number): void {
    this.request?.unsubscribe();
    // Ein laufender Puffer bleibt: die abgeloeste Anfrage hat seine Meldungen womoeglich nicht mehr gelesen.
    this.heldLiveEvents ??= [];
    this.request = this.ciMapping
      .list(this.projectId(), this.runId(), {
        executionId: this.executionId(),
        ...reportTestFilterQuery(this.activeChip(), this.search()),
        page,
        size: PAGE_SIZE,
      })
      .subscribe({
        next: (result) => {
          if (this.applyFilterCounts(result)) return;
          this.page = page;
          this.filteredTotal.set(result.filteredTotal);
          this.hasReportData.set(result.hasReportData);
          this.live.set(result.live);
          this.items.update((current) => (page === 0 ? result.items : [...current, ...result.items]));
          this.loading.set(false);
          this.loadingMore.set(false);
          this.releaseLiveEvents();
        },
        error: () => {
          this.loading.set(false);
          this.loadingMore.set(false);
          this.failure.set(page === 0 ? 'first' : 'more');
          this.releaseLiveEvents();
        },
      });
  }

  private applyFilterCounts(result: ReportTestPage): boolean {
    this.counts.set(reportTestChipCounts(result));
    if (this.activeChip() !== null) return false;
    const chip = reportTestChipToPreselect(result);
    this.activeChip.set(chip);
    if (chip === 'all') return false;
    this.fetch(0);
    return true;
  }

  private watchSearch(destroyRef: DestroyRef): void {
    this.searchInput
      .pipe(
        debounceTime(REPORT_TEST_SEARCH_DELAY_MS),
        filter((value): value is string => value !== null),
        map(reportTestSearchTerm),
        filter((term) => term !== this.search()),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe((term) => {
        this.search.set(term);
        this.actionErrorKey.set(null);
        this.load();
      });
  }

  /**
   * Die Tabelle haengt am Feed ihrer Ansicht, statt selbst zu verbinden: der Runner haelt ihn
   * bereits, und eine zweite Verbindung auf dasselbe Topic braechte jede Meldung doppelt.
   */
  private followLiveFeed(): void {
    const feed = this.feed;
    if (!feed) return;
    feed.events.pipe(takeUntilDestroyed()).subscribe((event) => this.receiveLiveEvent(event));
    let lost = false;
    effect(() => {
      const connection = feed.connection();
      if (connection === 'lost') {
        lost = true;
      } else if (connection === 'connected' && lost) {
        lost = false;
        // Was waehrend der Trennung gemeldet wurde, kommt nicht nach; nur die Liste kennt es.
        untracked(() => this.load());
      }
    });
  }

  private receiveLiveEvent(event: TestRunLiveEvent): void {
    if (event.executionId !== this.executionId()) return;
    if (event.type === 'report-tests-replaced') {
      // Gepufferte Meldungen gelten den ersetzten Live-Zeilen; nachgespielt loesten sie nur weiteres Neuladen aus.
      this.discardHeldLiveEvents();
      this.load();
    } else if (event.type === 'report-test-updated') {
      this.holdOrApply(event);
    }
  }

  private holdOrApply(event: TestRunLiveEvent): void {
    if (!this.heldLiveEvents) {
      this.applyReportTest(event);
    } else if (this.heldLiveEventsOverflowed || this.heldLiveEvents.length >= MAX_HELD_LIVE_EVENTS) {
      // Ab hier ist ein Neuladen billiger als das Nachspielen, und der Puffer waechst nicht unbegrenzt.
      this.heldLiveEventsOverflowed = true;
      this.heldLiveEvents = [];
    } else {
      this.heldLiveEvents.push(event);
    }
  }

  /**
   * Was waehrend des Ladens gemeldet wurde, kann in der Antwort schon stehen oder fehlen. In
   * Reihenfolge nachgespielt, steht danach je Zeile ihre letzte Meldung, auch dort, wo die
   * Antwort einen aelteren Stand gelesen hat.
   */
  private releaseLiveEvents(): void {
    const held = this.heldLiveEvents ?? [];
    const overflowed = this.heldLiveEventsOverflowed;
    this.heldLiveEvents = null;
    this.heldLiveEventsOverflowed = false;
    if (overflowed) {
      if (this.failure() !== 'first') this.load();
      return;
    }
    held.forEach((event) => this.holdOrApply(event));
  }

  private discardHeldLiveEvents(): void {
    this.heldLiveEvents = null;
    this.heldLiveEventsOverflowed = false;
  }

  private applyReportTest(event: TestRunLiveEvent): void {
    const entry = event.reportTest;
    const counts = event.counts;
    if (!entry || !counts || this.failure() === 'first') return;
    this.announceLiveStatus(counts);
    // Vor der ersten Antwort gibt es keine Chips; deren Zahlen setzt dann die Antwort.
    this.counts.update((current) => current && reportTestChipCounts(counts));
    // Bis zur ersten Meldung zeigt die Liste die Fall-Ergebnisse; die Live-Zeilen bringt erst ein Neuladen.
    if (!this.live()) {
      this.load();
      return;
    }
    const index = this.items().findIndex((item) => item.id === entry.id);
    if (index >= 0) {
      this.items.update((current) =>
        current.map((item, at) =>
          at === index ? { ...entry, linkedBugKey: item.linkedBugKey, defects: item.defects } : item,
        ),
      );
      return;
    }
    const row: ReportTestItem = { ...entry, linkedBugKey: null };
    if (!reportTestMatchesFilter(row, this.activeChip(), this.search())) return;
    // Ohne die letzte Seite steht die Zeile womoeglich auf einer noch nicht geladenen und kaeme
    // dort mit „Weitere laden" ein zweites Mal.
    const lastPageLoaded = this.items().length >= this.filteredTotal();
    // Steht die Zeile schon auf einer noch nicht geladenen Seite, zaehlt das eins zu viel; bewusst
    // hingenommen, denn die naechste Seitenantwort setzt den Wert des Servers.
    this.filteredTotal.update((total) => total + 1);
    if (lastPageLoaded) {
      this.items.update((current) => [...current, row]);
    }
  }

  private announceLiveStatus(counts: ReportTestLiveCounts): void {
    this.pendingLiveStatus = { reported: counts.total, failed: counts.statusCounts.failed };
    if (this.liveStatusTimer) return;
    const wait = this.liveStatusAnnouncedAt + LIVE_STATUS_INTERVAL_MS - Date.now();
    if (wait <= 0) {
      this.publishLiveStatus();
      return;
    }
    this.liveStatusTimer = setTimeout(() => {
      this.liveStatusTimer = undefined;
      this.publishLiveStatus();
    }, wait);
  }

  private publishLiveStatus(): void {
    this.liveStatus.set(this.pendingLiveStatus);
    this.liveStatusAnnouncedAt = Date.now();
  }
}
